import React from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { updateQuantity, removeFromCart } from "../../store/cartSlice";
import { Minus, Plus } from "lucide-react";
import { calculatePrice, formatDisplayPrice } from "../../utils/priceHandler";
import useLoadDBImage from "../../hooks/useLoadDBImage";
import ProductPrice from "../product/ProductPrice";
import trash_small from "/icons/trash-small.svg";

const BundleCartItem = ({ bundle }) => {
  const dispatch = useDispatch();
  const submittedPromoCodes = useSelector(state => state.cart.submittedPromoCodes);
  const rawTotal = bundle.bundleRawTotal * bundle.quantity;
  const subtotal = calculatePrice(bundle.bundleTotal, bundle.quantity, false, submittedPromoCodes)
  const savings = bundle.bundleDiscount * bundle.quantity;


  const handleQuantityChange = (change) => {
    if (bundle.quantity + change <= 0) {
      bundle.products.forEach(item => {
        dispatch(removeFromCart(item.cartId));
      });
    } else {
      bundle.products.forEach(item => {
        dispatch(updateQuantity({ cartId: item.cartId, change }));
      });
    }
  };

  const handleRemoveBundle = () => {
    bundle.products.forEach(item => {
      dispatch(removeFromCart(item.cartId));
    });
  };

  const bundleProductItem = (item) => {
    const imageUrl = useLoadDBImage("onProductImages", null, item.id, item.imageId);

    return (
      <div className="flex gap-3 py-2 border-b border-gray-200 last:border-b-0">
        <div className="w-12 h-12 min-w-12 bg-secondary-light rounded-10 overflow-hidden">
          {imageUrl && <img src={imageUrl} alt={item.name} className="w-full" />}
        </div>
        <div className="flex-1">
          <p className="text-sm font-medium mb-1">{item.name} | <span className="capitalize">{item.tag}</span></p>
          {item.petName && <p className="text-xs text-gray-500 mb-0">Name: {item.petName}</p>}
        </div>
        <div className="text-right">
          <ProductPrice price={item.price} quantity={1} isSubscribed={item.subscription} className="smaller-prices" />
        </div>
      </div>
    );
  };


  return (
    <div className="cart-item flex flex-col bg-white rounded-15 p-2 gap-2">
      <div className="flex gap-4">

        <div className="flex gap-2 justify-between w-full">
          <div className='flex items-start gap-4 gap-y-1 mb-2'>
            <p className="text-lg font-medium mb-0">{bundle.name || 'Product Bundle'}</p>
            {savings > 0 && <p className='saved mb-0 whitespace-nowrap'>Save ${savings}</p>}
          </div>
          <button onClick={handleRemoveBundle} className="remove-btn flex items-center gap-1">
            <img src={trash_small} alt="Remove" />
          </button>
        </div>
      </div>

      <div className="flex flex-col gap-2">
        {bundle.products.map((item) => (
          <div key={item.cartId}>
            {bundleProductItem(item)}
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm">Quantity:</span>
          <div className="quantity-controls flex items-center gap-2 border rounded-lg p-1">
            <button onClick={() => handleQuantityChange(-1)} className="quantity-btn px-1 py-1">
              <Minus size={14} />
            </button>
            <span className="text-sm font-semibold">{bundle.quantity}</span>
            <button onClick={() => handleQuantityChange(1)} className="quantity-btn px-1 py-1">
              <Plus size={14} />
            </button>
          </div>
        </div>
        <div className="text-right">
          <div className='product-price'>
            <span className="text-mid color-primary">{formatDisplayPrice(subtotal)}</span>
            <span className="ml-2 text-sm text-gray-500 line-through">
              {formatDisplayPrice(rawTotal)}
            </span>
          </div>

        </div>
      </div>
    </div>
  );
};

BundleCartItem.propTypes = {
  bundle: PropTypes.shape({
    name: PropTypes.string,
    quantity: PropTypes.number.isRequired,
    bundleRawTotal: PropTypes.number.isRequired,
    bundleTotal: PropTypes.number.isRequired,
    bundleDiscount: PropTypes.number.isRequired,
    products: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number.isRequired,
      cartId: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      tag: PropTypes.string,
      petName: PropTypes.string,
      price: PropTypes.number.isRequired,
      subscription: PropTypes.bool,
      imageId: PropTypes.string
    })).isRequired
  }).isRequired
};

export default BundleCartItem;